import {
    Auth,
    DataStore
} from "aws-amplify";
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    IconButton,
    Link,
    Paper,
    Tooltip,
    Typography,
    lighten
} from "@mui/material";
import {
    useEffect,
    useState
} from "react";

import { ClientLog } from "../../models";
import { CognitoUser } from "amazon-cognito-identity-js";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { ExpandableDisplayText } from "../../componentsV2/misc/ExpandableDisplayText";
import { FallbackProps } from "react-error-boundary";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import HomeIcon from '@mui/icons-material/Home';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { awsRum } from "../../App";
import { useSnackbar } from "notistack";

export const TENERA_SUPPORT_EMAIL = "support@tenerapro.com";
const TENERA_DEVELOPER_EMAIL = "developer@tenerapro.com";

type DebugInfo = {
    errorMessage: string;
    stack: string;
    path: string;
    timestamp: string;
};
const getErrorReportEmailBody = (debugInfo: DebugInfo) => {
    return `
ERROR REPORT
------------
Error Message: ${debugInfo.errorMessage}

DEBUGGING INFORMATION:
- Timestamp: ${debugInfo.timestamp}
- Path: ${debugInfo.path}

STACK TRACE:
${debugInfo.stack}

Please describe what you were doing when the error occurred:
`;
};

/**
 * ErrorFallback is treated as class component where hooks are not allowed.
 * This is a workaround to use hooks in ErrorFallback.
 */
const ErrorFallbackContent = ({ error, resetErrorBoundary }: FallbackProps) => {
    const [debugInfo] = useState<DebugInfo>({
        errorMessage: error.message,
        stack: error.stack,
        path: window.location.href,
        timestamp: new Date().toLocaleString()
    });
    const [user, setUser] = useState<CognitoUser | null>(null);
    const snackbar = useSnackbar();

    useEffect(() => {
        awsRum?.recordError(error);
        Auth.currentAuthenticatedUser()
            .then((user: CognitoUser) => {
                setUser(user);
                const clientLog = new ClientLog({
                    details: JSON.stringify(debugInfo),
                    message: error.message,
                    severityLevel: "ERROR",
                    componentName: "",
                    userAgent: navigator.userAgent,
                    userId: user.getUsername()
                });
                DataStore.save(clientLog);
            })
            .catch((error) => {
                const clientLog = new ClientLog({
                    details: JSON.stringify(debugInfo),
                    message: error.message,
                    severityLevel: "ERROR",
                    componentName: "",
                    userAgent: navigator.userAgent,
                });
                DataStore.save(clientLog);
            });
    }, []);

    const mailtoLink = `mailto:${TENERA_DEVELOPER_EMAIL}` +
        `?subject=${encodeURIComponent(`Error Report${user ? ` - User: ${user.getUsername()}` : ""}`)}` +
        `&body=${encodeURIComponent(getErrorReportEmailBody(debugInfo))}`;

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(mailtoLink);
            snackbar.enqueueSnackbar("Email copied to clipboard", { variant: "success" });
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to copy email", { variant: "error" });
        }
    };

    const copyDebugInfo = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(debugInfo));
            snackbar.enqueueSnackbar("Debug info copied to clipboard", { variant: "success" });
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to copy debug info", { variant: "error" });
        }
    };

    return (
        <Box width="100%" display="flex" justifyContent="center">
            <Card elevation={3} sx={{ width: '100%', borderRadius: 2 }}>
                <CardContent sx={{ p: 4 }}>
                    {/* Error Icon */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 3
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: '50%',
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: theme => lighten(theme.palette.error.light, 0.5)
                            }}
                        >
                            <WarningAmberIcon color="error" fontSize="large" />
                        </Box>
                    </Box>

                    {/* Error Title */}
                    <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold">
                        Oops! Something went wrong
                    </Typography>

                    {/* Error Message */}
                    <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
                        We encountered an unexpected error while processing your request.
                        Our team has been notified and is working to fix it.
                    </Typography>

                    {/* Contact Section */}
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 1
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Need immediate assistance? Contact our support team:
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<EmailIcon />}
                                    onClick={() => window.open(mailtoLink, '_blank')}
                                >
                                    Email Support
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ContentCopyIcon />}
                                    onClick={copyEmail}
                                >
                                    Copy Email
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Debug Information */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Debug Information</Typography>
                            <Tooltip title="Copy debug info">
                                <IconButton size="small" onClick={copyDebugInfo}>
                                    <FileCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                bgcolor: 'grey.50',
                                fontFamily: 'monospace',
                                fontSize: '0.75rem'
                            }}
                        >
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Error Message:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        {debugInfo.errorMessage}
                                    </Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Stack:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <ExpandableDisplayText
                                        description={debugInfo.stack}
                                        linesToDisplayWhenCollapsed={10}
                                        maxLineToDisplayWhenExpanded={30}
                                        typographyProps={{
                                            variant: "caption",
                                            sx: { fontFamily: 'monospace' }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Path:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily: 'monospace',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block'
                                        }}
                                    >
                                        {debugInfo.path}
                                    </Typography>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="caption" color="text.secondary">Timestamp:</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily: 'monospace',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block'
                                        }}
                                    >
                                        {debugInfo.timestamp}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Paper>

                    {/* Navigation Options */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<ReplayIcon />}
                            onClick={resetErrorBoundary}
                            sx={{ mb: 2 }}
                        >
                            Retry
                        </Button>

                        <Link
                            href="/"
                            underline="hover"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'text.secondary'
                            }}
                        >
                            <HomeIcon fontSize="small" />
                            <Typography variant="body2">Return to homepage</Typography>
                        </Link>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
    return (
        <ErrorFallbackContent error={error} resetErrorBoundary={resetErrorBoundary} />
    );
};;